/**
 * Storage Caching Utility
 * 
 * Caches localStorage/sessionStorage reads in memory to avoid expensive I/O operations.
 * Based on React Best Practices: js-cache-storage
 * 
 * Performance: Reduces 5-20ms per storage access
 */

const storageCache = new Map<string, string | null>();

/**
 * Get item from localStorage with caching
 * @param {string} key - Storage key
 * @returns {string|null} - Cached value or null
 */
export function getCachedLocalStorage(key: string): string | null {
  if (!storageCache.has(key)) {
    try {
      const value = localStorage.getItem(key);
      storageCache.set(key, value);
    } catch (error) {
      console.error('localStorage access error:', error);
      return null;
    }
  }
  return storageCache.get(key);
}

/**
 * Set item to localStorage and update cache
 * @param {string} key - Storage key
 * @param {string} value - Value to store
 */
export function setCachedLocalStorage(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
    storageCache.set(key, value);
  } catch (error) {
    console.error('localStorage write error:', error);
  }
}

/**
 * Remove item from localStorage and cache
 * @param {string} key - Storage key
 */
export function removeCachedLocalStorage(key: string): void {
  try {
    localStorage.removeItem(key);
    storageCache.delete(key);
  } catch (error) {
    console.error('localStorage remove error:', error);
  }
}

/**
 * Clear entire localStorage and cache
 */
export function clearCachedLocalStorage(): void {
  try {
    localStorage.clear();
    storageCache.clear();
  } catch (error) {
    console.error('localStorage clear error:', error);
  }
}

/**
 * Invalidate cache for a specific key
 * @param {string} key - Storage key to invalidate
 */
export function invalidateStorageCache(key: string): void {
  storageCache.delete(key);
}

/**
 * Invalidate entire cache (keeps localStorage intact)
 */
export function invalidateAllStorageCache(): void {
  storageCache.clear();
}

// Listen for storage changes from other tabs
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key) {
      // Specific key changed
      storageCache.delete(e.key);
    } else {
      // Entire storage cleared
      storageCache.clear();
    }
  });

  // Clear cache when page becomes visible (in case external changes occurred)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      // Optionally invalidate cache on tab focus
      // storageCache.clear();
    }
  });
}

interface StorageUtils {
  get: typeof getCachedLocalStorage;
  set: typeof setCachedLocalStorage;
  remove: typeof removeCachedLocalStorage;
  clear: typeof clearCachedLocalStorage;
  invalidate: typeof invalidateStorageCache;
  invalidateAll: typeof invalidateAllStorageCache;
}

const storageUtils: StorageUtils = {
  get: getCachedLocalStorage,
  set: setCachedLocalStorage,
  remove: removeCachedLocalStorage,
  clear: clearCachedLocalStorage,
  invalidate: invalidateStorageCache,
  invalidateAll: invalidateAllStorageCache
};

export default storageUtils;
